// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import Notify from "@guestsupply/App/Components/Notify/Notify";
import xPostalCode from "@guestsupply/App/Handlers/XNG/PosatCodeHandler";
import setPostalCodeModal from "@guestsupply/App/Handlers/XNG/PostalCodeModalHandler";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import TextField from "@guestsupply/MobiusOverrides/TextField/TextField";
import { xValidateOnSubmit } from "@guestsupply/XDL/Utilities";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal from "@insite/mobius/Modal";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

export interface AdditionalAvailabilityInfoModel {
    productId: string;
    longMessage: string;
    messageType: number;
    warehouseName?: string;
}
interface OwnProps {
    setIsModalOpen?: Dispatch<SetStateAction<boolean>>;
    isModalOpen?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    return {
        isAuthenticated: session.isAuthenticated,
        isLoadingPostalCode: (state as CustomAppState).xCommon.loadingOverlay,
        isUsWebsite: getSettingsCollection(state).customSettings?.isUsWebSite,
        isOpen: (state as CustomAppState).xPostalCodeModal.isOpen,
        currentPostalCode: getBillToState(state, session.billToId).value?.postalCode,
    };
};

const mapDispatchToProps = {
    xPostalCode,
    setPostalCodeModal,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PostalCodeModalStyles {
    setPostalCodeButton?: ButtonPresentationProps;
}

export const postalCodeModalStyles: PostalCodeModalStyles = {};

const styles = postalCodeModalStyles;

const StyledForm = getStyledWrapper("form");

const PostalCodeModal = ({
    xPostalCode,
    isLoadingPostalCode,
    isUsWebsite,
    setPostalCodeModal,
    isOpen,
    currentPostalCode,
    setIsModalOpen,
    isModalOpen,
}: Props) => {
    const [postalCode, setPostalCode] = useState(currentPostalCode || "");
    const [postalCodeError, setPostalCodeError] = useState("");

    const isSubmittingPostalCode = isLoadingPostalCode?.includes("PostalCodeModal");

    const resetFields = () => {
        setPostalCode(currentPostalCode || "");
        setPostalCodeError("");
    };

    const handleCloseModal = () => {
        resetFields();
        setPostalCodeModal({ isOpen: false });
        setIsModalOpen?.(false);
    };

    const handlePostalChange = (postal: string) => {
        setPostalCode(postal);
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const errors = xValidateOnSubmit(formId);
        if (errors.length) {
            return;
        }
        xPostalCode({ postalCode });
    };

    const formId = "postalCodeForm";

    useEffect(() => {
        setPostalCode(currentPostalCode || "");
    }, [currentPostalCode]);

    return (
        <Modal
            sizeVariant="medium"
            headline={currentPostalCode ? translate("Edit your Postal Code") : translate("Enter your Postal Code")}
            isOpen={isOpen || isModalOpen}
            handleClose={handleCloseModal}
        >
            {/* <LoadingOverlay loading={isSubmittingPostalCode}> */}
            <StyledWrapper>
                <Notify type="info" data-test-selector="ProductListing_EnterPostalCode">
                    {siteMessage("Guest_SetPostalCodeHelpText", "Enter Your Postal Code")}
                </Notify>
                <StyledForm onSubmit={handleSubmit} noValidate>
                    <TextField
                        label="Postal Code"
                        type={isUsWebsite ? "postalCode" : "postalCodeCA"}
                        placeholder={translate(isUsWebsite ? "99800" : "A1A 1A1")}
                        value={postalCode}
                        required
                        disabled={isSubmittingPostalCode}
                        onChange={e => handlePostalChange(e.target.value)}
                        error={postalCodeError}
                        data-test-selector="PostalCode"
                        {...{ formId }}
                    />
                    <Button
                        {...styles.setPostalCodeButton}
                        disabled={isSubmittingPostalCode}
                        aria-label={translate("Set My Postal Code")}
                        type="submit"
                    >
                        {translate("Set My Postal Code")}
                    </Button>
                </StyledForm>
            </StyledWrapper>
            {/* </LoadingOverlay> */}
        </Modal>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(PostalCodeModal);
